// Airbrush stateless hash + radial density falloff
// (airbrush-design-2026-08-15.md §1 / §2).
//
// ## Why stateless (no PRNG object, no per-stroke counter mutated by the
// kernel)
//
// A stateful PRNG shared across concurrently-drawing users breaks the moment
// two strokes interleave — the WS arrival order differs per client, so
// "how many times has the PRNG been advanced" diverges between senders and
// snapshot replay (design §1.1). Instead every pixel's ink/no-ink decision
// is a PURE FUNCTION of `(seed, pointIndex, i, cellX, cellY)`:
//
//   h = hash(seed, pointIndex, i, cellX, cellY)   // u32, integer-only
//   t = h >>> 16                                  // u16 (0..65535)
//
// Nothing in that domain depends on "who else drew" or "what order things
// arrived in" or "how many pixels were skipped" — so it is structurally
// immune to the interleaving hazard, not just tested against it.
//
// ## Why the threshold is 16-bit, not 8-bit (2026-08-20 revision)
//
// The gate used to be the hash's top BYTE (`h >>> 24`, 0..255) compared
// against an 8-bit `peakDensityLevel` (0..256). That only gives 256 distinct
// per-dab ink probabilities, and the sparse end of the slider needs
// probabilities as small as 0.05%-1% (see `airbrushPeakDensityLevel`'s doc
// for why) — which round to `level` 0..13 in an 8-bit domain. Combined with
// the radial falloff's `floor()` (`airbrushDensityLevelAt`), a small integer
// `peakLevel` divided across a stamp's radius floors to EXACTLY 0 for every
// pixel except the dead centre, i.e. "density=0.1" painted literally nothing
// visible. Widening the gate to 16 bits (0..65535) gives ~656x more
// resolution at the low end (a 1% probability is `level≈655`, not `level≈3`),
// so the falloff no longer floors to 0 until genuinely at/past the stamp
// edge — the "invisible" cliff is gone by construction, not by curve-shaping
// around it (see `airbrushPeakDensityLevel`'s doc for how this simplified
// that function too).
//
// ## Determinism (ABSOLUTE — airbrush-design-2026-08-15.md §5)
//
// - **Integer-only.** JS bitwise/`Math.imul` operate on SIGNED 32-bit ints;
//   every intermediate is forced back to unsigned via `>>> 0` so the exact
//   same bit pattern flows through every step. The Rust mirror
//   (`drawing-core-wasm/src/airbrush.rs`) uses `u32` + `wrapping_mul`, which
//   is bit-identical to `Math.imul(...) >>> 0` for the same inputs.
// - **u16 extraction is `h >>> 16`** — never a float division
//   (`h / 65536`). Float division would reintroduce IEEE rounding into
//   a path that's supposed to be pure integer bit-manipulation, and (more
//   importantly) `4294967296` overflows safe-integer precision concerns for
//   no benefit — the top-half-word shift is both simpler and exactly what
//   the design doc specifies.
// - The mix step (`mix`) is a golden-ratio multiplicative hash combiner;
//   the finalizer (`fmix32`) is the standard MurmurHash3 32-bit finalizer.
//   Both are widely-published, trivially portable to `wrapping_mul`, and
//   good enough avalanche for a visual "does this pixel get ink" decision
//   (this is not a cryptographic or collision-resistance requirement).
/** Default cell-quantisation grain when `penParams.grain` is omitted (§1.3 — 2×2 cell, mist-leaning). */
export const AIRBRUSH_DEFAULT_GRAIN = 1;
/** Default spray density when `penParams.density` is omitted (mid-density, mirrors the tone pen's default). */
export const AIRBRUSH_DEFAULT_DENSITY = 0.5;
/**
 * `density` (0..1, `penParams.density`) → `peakDensityLevel` (0..65536, the
 * per-pixel ink-gate compared against the hash threshold word,
 * `airbrushDensityLevelAt`'s `peakLevel` argument). THE single quantisation
 * point for airbrush density — every caller (JS canvas kernel
 * `createAirbrushStampRenderer`, Wasm ABI resolver
 * `resolveAirbrushPeakDensityLevel`) MUST go through this function, never
 * re-derive the mapping (mirrors `toneDensityLevel` being the tone pen's
 * single point — airbrush now has its OWN dedicated point instead of
 * reusing tone's, see below for why).
 *
 * ## Why NOT a straight line or a hand-picked power curve (2026-08-20 rewrite)
 *
 * A single stroke sweeps a dab past every pixel under it roughly `penWidth`
 * times (each pass an INDEPENDENT ink/no-ink coin flip, the hash's whole
 * point — see the module header), so the probability a pixel ends up inked
 * SATURATES: `coverage = 1 - (1 - level/65536)^penWidth`. That is a sharply
 * concave function of `level` — most of the achievable coverage range is hit
 * by a tiny `level`, and the remaining stretch needs an enormous `level`
 * increase for a small coverage gain. Every earlier revision of this
 * function (straight line, single power curve, hand-split two-power-curve
 * with a hand-picked exponent) tried to counteract that concavity by
 * SHAPING `level(density)` from the `level` side, picked "by eye" against a
 * handful of spot-measured coverage numbers. Each fix moved the visible
 * problem to a different part of the slider (dead low end → flat middle →
 * sudden jump near the top) because a hand-picked exponent has no reason to
 * match the ACTUAL saturation curve at the reference pen width — it is
 * fundamentally guessing the inverse of a function that was never measured
 * as a whole.
 *
 * ## The fix: calibrate against measured coverage, then invert
 *
 * Instead of guessing a `level`-space shape, this curve is built by:
 *
 * 1. Measuring `coverage(level)` directly — every `level` in a fine grid,
 *    swept as an actual stroke through the SAME engine/rasterizer path a
 *    real stroke uses (reference stroke: width 60px, `grain=1`, the "fraction
 *    of the swept band with any ink" metric `airbrushStamp.test.ts`'s
 *    `airbrush density → coverage calibration` block re-measures against —
 *    see the calibration table below for the frozen inverted values).
 * 2. Picking the TARGET coverage curve to be a straight line in `density`:
 *    `target(density) = M * density`, where `M` is the measured coverage at
 *    `level=65536` (the achievable ceiling for the reference stroke — a
 *    stroke can never cover 100% of its own swept band edge-to-edge because
 *    `AIRBRUSH_FALLOFF_EXPONENT` intentionally tapers ink to ~0 at the very
 *    edge of the stamp radius, by design — see that constant's doc). Scaling
 *    the target to the ACHIEVABLE ceiling (not a literal 100%) is what keeps
 *    `level(density)` strictly increasing with no flat stretch near the top:
 *    a target that asked for more coverage than any `level` can produce
 *    would force `level` to pin at 65536 for a whole trailing range of
 *    `density`, reintroducing exactly the kind of "dead slider" bug this
 *    revision fixes.
 * 3. INVERTING the measured `coverage(level)` curve at 32 evenly-spaced
 *    `density` grid points (`AIRBRUSH_DENSITY_CALIBRATION_TABLE`, 33 values
 *    incl. both endpoints) — i.e. finding, for each target coverage, the
 *    `level` that actually produces it, by table lookup + interpolation
 *    against the measured data (never re-derived from a formula).
 * 4. Runtime interpolates LINEARLY between the two bracketing table entries
 *    for an arbitrary `density` in `[0, 1]` — plain `+ - *` and one final
 *    `Math.round`, the same determinism class as the rest of this module
 *    (see the Determinism note below).
 *
 * Verified (not just asserted): re-running the reference stroke through this
 * table produces measured coverage within ~0.3 percentage points of the
 * straight-line target at every density from 0 to 1 (fine 5%-density-step
 * sweep) — see `airbrushStamp.test.ts`'s `airbrush density → coverage
 * calibration` block, which re-derives `M` from the SAME live rasterizer
 * this table was built from (not a hardcoded duplicate of it).
 *
 * ## Two hard endpoints preserved
 *
 * `density=0` → `level=0` (no ink) and `density=1` → `level=65536` (the
 * per-pixel gate's maximum — "always inks" at the exact stamp centre,
 * `airbrushDensityLevelAt`'s doc) are exact table endpoints, unchanged from
 * every earlier revision of this function and pinned by
 * `airbrushHash.test.ts`.
 *
 * ## Determinism
 *
 * Table lookup is exact (integer array index). Interpolation is `+ - *` and
 * one final `Math.round` — no `Math.pow`/`Math.sqrt` (transcendental
 * functions the spec does NOT require to be bit-identical across engines,
 * unlike `+ - * /` and `Math.round`, which IEEE-754 pins exactly). No Rust
 * mirror is needed — the ABI only ever carries the ALREADY-quantised integer
 * `peakDensityLevel` across the client/server boundary (see
 * `WasmStampRenderer.ts` `resolveAirbrushPeakDensityLevel`; Rust never
 * re-derives this from a raw float, so there is no JS/Rust parity risk to
 * begin with).
 *
 * ## Known limitation: the calibration is anchored to ONE pen width
 *
 * `N` (how many times a dab sweeps past a given pixel) scales with pen
 * width, so the saturating `coverage(level)` curve this table inverts is
 * itself width-dependent. The table is calibrated against a 60px-wide
 * reference stroke only; narrower/wider strokes drift from the "density
 * doubles → coverage doubles" proportionality this table targets (measured
 * drift reported in the density-calibration report — not compensated for,
 * by design: a per-width correction is a separate, larger change and this
 * revision's scope is fixing the 60px reference curve's shape).
 */
const AIRBRUSH_DENSITY_CALIBRATION_TABLE = [
    0, 81, 159, 263, 345, 436, 539, 627, 732, 834, 947, 1106, 1261, 1440, 1625, 1817, 2042, 2278,
    2608, 2974, 3326, 3736, 4411, 5115, 6020, 7221, 8655, 10759, 13645, 18985, 25479, 40034, 65536,
];
/** `AIRBRUSH_DENSITY_CALIBRATION_TABLE.length - 1` — 32 equal `density` steps (33 grid points). */
const AIRBRUSH_DENSITY_TABLE_STEPS = AIRBRUSH_DENSITY_CALIBRATION_TABLE.length - 1;
export const airbrushPeakDensityLevel = (density) => {
    const d = density < 0 ? 0 : density > 1 ? 1 : density;
    const x = d * AIRBRUSH_DENSITY_TABLE_STEPS;
    const i0 = x >= AIRBRUSH_DENSITY_TABLE_STEPS ? AIRBRUSH_DENSITY_TABLE_STEPS - 1 : Math.floor(x);
    const frac = x - i0;
    const lo = AIRBRUSH_DENSITY_CALIBRATION_TABLE[i0];
    const hi = AIRBRUSH_DENSITY_CALIBRATION_TABLE[i0 + 1];
    const level = Math.round(lo + (hi - lo) * frac);
    return level < 0 ? 0 : level > 65536 ? 65536 : level;
};
/**
 * Golden-ratio multiplicative mix step. `h ^= k` folds a new integer input
 * into the running hash state; the multiply + shift-xor spreads that
 * change across all 32 bits (avalanche) before the next input is folded in.
 * Every operator here (`^`, `Math.imul`, `>>>`) treats its operands as
 * 32-bit integers per the ECMAScript spec, so this is exact integer math —
 * no float ever enters.
 */
const mix = (h, k) => {
    let x = (h ^ k) >>> 0;
    x = Math.imul(x, 0x9e3779b1) >>> 0;
    x = (x ^ (x >>> 16)) >>> 0;
    return x;
};
/**
 * MurmurHash3 32-bit finalizer. Mirrors Rust `fmix32` in
 * `drawing-core-wasm/src/airbrush.rs` operation-for-operation (same
 * constants, same shift amounts) so the two implementations are
 * byte-identical for identical inputs — verified by
 * `airbrushHash.test.ts` (JS vector) and `airbrush.rs` unit tests (SAME
 * hardcoded vectors, ported by hand).
 */
const fmix32 = (hIn) => {
    let h = hIn >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    h = Math.imul(h, 0x85ebca6b) >>> 0;
    h = (h ^ (h >>> 13)) >>> 0;
    h = Math.imul(h, 0xc2b2ae35) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    return h >>> 0;
};
/**
 * `h = hash(seed, pointIndex, i, cellX, cellY)` (airbrush-design-2026-08-15.md
 * §1). Every argument is coerced through `>>> 0` at the point of use inside
 * `mix`/`fmix32`, so out-of-range or negative inputs wrap the same way the
 * Rust `u32` mirror wraps — callers still SHOULD only ever pass non-negative
 * in-range values (canvas coords, counters), this is just belt-and-braces
 * determinism, not a validation layer.
 */
export const airbrushHash = (seed, pointIndex, i, cellX, cellY) => {
    let h = seed >>> 0;
    h = mix(h, pointIndex >>> 0);
    h = mix(h, i >>> 0);
    h = mix(h, cellX >>> 0);
    h = mix(h, cellY >>> 0);
    return fmix32(h);
};
/**
 * `t = h >>> 16` — the u16 threshold word the kernel compares against the
 * per-pixel density level (`airbrushDensityLevelAt`). See the module header
 * for why this is a bit-shift, never a float division, and why it's 16 bits
 * (top half-word) rather than the original 8-bit top byte.
 */
export const airbrushThresholdWord = (seed, pointIndex, i, cellX, cellY) => airbrushHash(seed, pointIndex, i, cellX, cellY) >>> 16;
/**
 * Falloff steepness exponent — `level = peak * u^AIRBRUSH_FALLOFF_EXPONENT`
 * where `u = (r² - d²) / r²` (see `airbrushDensityLevelAt`'s doc for the full
 * derivation). Named/tunable on purpose (2026-08-20 revision): the ORIGINAL
 * `k=1` shape (`level = peak * u`, i.e. `peak * (1 - (d/r)²)`) is smooth in
 * `level`-space but produces a visually flat "plateau + cliff" in COVERAGE
 * (`1 - (1-p)^N`) once a stroke's many overlapping stamps saturate the
 * near-centre probability to ~100% — see the module's coverage-plateau
 * regression test for the concrete before/after numbers this constant fixes.
 * Raising `k` compresses the near-centre `level` (and therefore per-stamp
 * ink probability) enough that the saturating accumulation across overlapping
 * stamps no longer clips flat before the edge. `k=3` was the smallest integer
 * that visibly restored "continuously thinning outward, no plateau, no
 * cliff" against the measured coverage tables (see `airbrushHash.test.ts` /
 * the density-calibration report) — raise it further only after re-measuring
 * coverage, never by feel alone.
 */
export const AIRBRUSH_FALLOFF_EXPONENT = 3;
/**
 * Radial density falloff (§2.3): peak at the stamp centre, 0 at/beyond the
 * radius. `densityLevel = f(dx² + dy²)` — **no `sqrt`, no trig, no
 * `Math.pow`** (integer/float `+ - * /` + `floor` only, the same determinism
 * class the AA SDF path already relies on for `+ - * / sqrt`; here we
 * deliberately drop the `sqrt`/`pow` per the design doc — the exponent is
 * applied via plain repeated multiplication, see below).
 *
 * Shape: let `u = (r² - d²) / r²` (0 at the edge, 1 at the centre — the
 * SAME ratio the original `k=1` falloff used directly). `level = floor(peak
 * * u^k)` for `d² < r²`, else `0`, where `k = AIRBRUSH_FALLOFF_EXPONENT`.
 * `u^k` is computed by plain multiplication (`u * u * ... `, `k` times) —
 * never `Math.pow` — so this stays in the same "integer/float `+ - * /`"
 * determinism class as the rest of this module, just with the ratio raised
 * to a small integer power before scaling by `peak`.
 *
 * ## Why `k > 1` (2026-08-20 revision — coverage plateau + cliff)
 *
 * The original `k=1` falloff (`peak * (1 - (d/r)²)`) is smooth and
 * monotonic in `level`-space, but a single stroke sweeps a dab past every
 * pixel under it roughly `penWidth` times, so the pixel's COVERAGE
 * probability is `1 - (1 - level/65536)^N` (`airbrushPeakDensityLevel`'s doc
 * derives the same saturation) — a function that clips to ~100% the moment
 * `level` crosses a fairly low threshold. Because `k=1`'s falloff stays
 * near-peak across most of the stamp's radius (`1 - (d/r)²` is `>= 0.75` for
 * `d/r <= 0.5`), the ACCUMULATED coverage was measured flat at ~64-72%
 * (density=0.5) or pinned at 100% (density=0.8) all the way out to
 * `d/r≈0.7-0.8`, then falling off a cliff over the last fifth of the radius
 * — a "plateau + cliff" that reads as a hard-edged disc, not an airbrush
 * mist. Raising `k` pushes the `level`-space falloff down much faster near
 * the centre (`u^3` is `0.42` at `d/r=0.5` vs `u^1`'s `0.75`), which — via
 * the SAME saturating-coverage relationship — is what actually produces a
 * continuously-thinning-outward coverage curve instead of a plateau. See
 * `AIRBRUSH_FALLOFF_EXPONENT`'s doc for why `k=3` specifically, and
 * `airbrushHash.test.ts` / the density-calibration report for the measured
 * before/after coverage tables.
 *
 * `r` is clamped to a 0.5px floor (mirrors the AA kernel's own radius floor)
 * so a hairline-width airbrush stroke doesn't divide by zero.
 *
 * Clamped to 65535 (the max meaningful threshold-compare level — `t` is a
 * 0..65535 word, so 65535 already means "always inks"; 16-bit domain,
 * 2026-08-20 revision, see the module header).
 */
export const airbrushDensityLevelAt = (dx, dy, r, peakLevel) => {
    const rr = r < 0.5 ? 0.5 : r;
    const r2 = rr * rr;
    const d2 = dx * dx + dy * dy;
    if (d2 >= r2)
        return 0;
    const u = (r2 - d2) / r2;
    let uk = u;
    for (let n = 1; n < AIRBRUSH_FALLOFF_EXPONENT; n++)
        uk *= u;
    const level = Math.floor(peakLevel * uk);
    return level > 65535 ? 65535 : level;
};
// ─── Scatter kernel (grain >= 1): round, irregularly-placed droplets ───────
//
// Revision 2026-08-20 — walks back §1.3's "cell-quantise, hash ONCE per
// cell" approach for grain >= 1. That approach hashed `nx >> grain` /
// `ny >> grain` (a world-fixed cellSize×cellSize block) to a SINGLE
// threshold byte, so the ink/no-ink decision for an ENTIRE cell was constant
// — real paint spatter is round and irregularly placed, not a tiling of
// axis-aligned squares on a world grid ("mosaic"; the same failure mode the
// texture `spray` preset was retired for, see `texture-brush-design.md §1`).
//
// Each grain-cell instead owns exactly one circular "droplet" — a centre
// jittered to an arbitrary position INSIDE the cell's own box, and a radius
// jittered between 0.5x and 1.0x the cell size. A pixel inks iff it falls
// inside ANY droplet whose cell could possibly reach it (searched via a 3×3
// neighbourhood — see `airbrushScatterInk`'s doc for why that's sufficient).
//
// `grain === 0` (cellSize = 1px) keeps the ORIGINAL single-hash-per-pixel
// dither (`airbrushIsInk` below) unchanged: at cellSize=1 there is no
// aggregation to begin with (every pixel already gets its own independent
// hash), so grain=0 was never mosaic-broken, and skipping the 3×3 scatter
// search there avoids a 9x hash-per-pixel cost at the finest grain setting
// for no visual benefit (a <1px droplet is indistinguishable from a single
// dithered pixel).
/**
 * Droplet radius bounds, expressed as a factor of the grain's cell size
 * (`airbrushCellSize`). **`MAX` must stay <= 1.0** — the 3×3 neighbour
 * search in `airbrushScatterInk` is only PROVABLY sufficient (no droplet
 * ever gets clipped at a cell boundary) while the radius never exceeds one
 * full cell width; see that function's doc comment for the closed-form
 * proof. Do not raise `MAX` past 1.0 without re-deriving the bound and
 * widening the search window to match.
 */
export const AIRBRUSH_DROPLET_RADIUS_MIN_FACTOR = 0.5;
export const AIRBRUSH_DROPLET_RADIUS_MAX_FACTOR = 1.0;
/** Cell size in px for a grain level: `1 << grain` (grain 0..3 → 1, 2, 4, 8px). */
export const airbrushCellSize = (grain) => 1 << grain;
/**
 * Derives one cell's droplet from its hash. Reuses a SINGLE `airbrushHash`
 * call per cell (not one call per derived quantity) — the centre-jitter X/Y
 * and gate threshold are independent lanes of the SAME 32-bit hash (bits
 * 0-7 / 8-15 for `jx`/`jy`, bits 16-31 for the 16-bit `gateThreshold`). This
 * keeps the scatter kernel's per-cell hash cost identical to the original
 * per-cell kernel's (one `airbrushHash` call), even though a pixel now
 * consults up to 9 cells.
 *
 * Widening the gate from 8 to 16 bits (module header) leaves only 16 bits
 * for `jx`/`jy`/`jr` combined, one fewer lane than the 3 they used to have.
 * Radius jitter (`jr`) is cosmetic — it only varies a droplet's rendered
 * size within a fixed 0.5x-1.0x-of-cell-size band (`AIRBRUSH_DROPLET_RADIUS_
 * *_FACTOR`), never whether/where a droplet inks — so instead of spending a
 * THIRD hash-derived byte on it, it's derived from the two bytes already in
 * hand: `jr = jx ^ jy`. This keeps the hash call count at one-per-cell (no
 * perf cost) and stays fully deterministic/portable to Rust (`^` on two
 * bytes); the only tradeoff is `jr` is no longer statistically independent
 * of the droplet's own position jitter, which is imperceptible for a
 * decorative radius wobble.
 *
 * `cellX`/`cellY` are the SIGNED cell coordinates (may be negative near
 * canvas edges when a neighbour cell is probed) — used as-is for the centre
 * math so the droplet lands at the correct signed world position. They are
 * ONLY wrapped to u32 (`>>> 0`) at the point they feed `airbrushHash`,
 * mirroring the Rust side's `as u32` two's-complement reinterpret cast — see
 * `airbrush.rs` `airbrush_droplet`.
 */
export const airbrushDroplet = (seed, pointIndex, i, cellX, cellY, cellSize) => {
    const h = airbrushHash(seed, pointIndex, i, cellX >>> 0, cellY >>> 0);
    const jx = h & 0xff;
    const jy = (h >>> 8) & 0xff;
    const jr = (jx ^ jy) & 0xff;
    const gateThreshold = h >>> 16;
    // Centre: uniformly placed inside the cell's own [0, cellSize) x [0,
    // cellSize) box. `jx * cellSize` is an exact integer (both operands are
    // small integers); dividing by 256 (a power of two) is exact in IEEE-754
    // double precision — no rounding drift between JS and Rust as long as the
    // operation order matches exactly (multiply, THEN divide).
    const centerX = cellX * cellSize + (jx * cellSize) / 256;
    const centerY = cellY * cellSize + (jy * cellSize) / 256;
    const radius = cellSize *
        (AIRBRUSH_DROPLET_RADIUS_MIN_FACTOR +
            (AIRBRUSH_DROPLET_RADIUS_MAX_FACTOR - AIRBRUSH_DROPLET_RADIUS_MIN_FACTOR) * (jr / 255));
    return { centerX, centerY, radius, gateThreshold };
};
/**
 * grain >= 1 scatter kernel. A pixel inks iff it falls inside any droplet
 * from the 3×3 neighbourhood of grain-cells around it.
 *
 * ## Why 3×3 is provably sufficient (no droplet clipped at a cell boundary)
 *
 * A droplet's centre is constrained inside its own `cellSize × cellSize`
 * box, and its radius never exceeds `cellSize`
 * (`AIRBRUSH_DROPLET_RADIUS_MAX_FACTOR = 1.0`). Pixel centres are
 * half-integer (`nx + 0.5`), so a pixel's offset from its own cell's near
 * edge is at most `cellSize - 0.5`. For a candidate cell `N >= 2` cells away
 * on EITHER axis, the smallest possible on-axis distance between the pixel
 * and any point in that cell's box is
 *
 *   N * cellSize - (cellSize - 0.5) = (N - 1) * cellSize + 0.5
 *
 * which for `N >= 2` is already `>= cellSize + 0.5`, strictly greater than
 * the `<= cellSize` max radius (with a comfortable 0.5px margin from the
 * pixel-centre discretisation — not an infinitesimal boundary case).
 * Euclidean distance is never smaller than either axis component alone, so
 * once EITHER axis offset is `>= 2` cells, the droplet cannot reach the
 * pixel regardless of the other axis. That leaves only `|dcx| <= 1` AND
 * `|dcy| <= 1` — i.e. exactly the pixel's own cell and its 8 immediate
 * neighbours — as candidates that can EVER matter. This is a closed-form
 * bound, not a tuned constant: do not shrink the search window below 3×3
 * (or raise `AIRBRUSH_DROPLET_RADIUS_MAX_FACTOR` past 1.0) without
 * re-deriving it. See `airbrushScatterInk.test` cell-boundary regression for
 * the empirical backstop.
 *
 * ## Density: gated at the DROPLET's centre, not the pixel
 *
 * The per-stamp radial falloff (`airbrushDensityLevelAt`) decides whether a
 * cell's droplet exists AT ALL, evaluated at the droplet's own (jittered)
 * centre relative to the STAMP centre — not at the pixel being tested. This
 * keeps every droplet that DOES exist perfectly round: gating per PIXEL
 * instead would chop droplets that straddle the falloff's effective edge
 * into crescents (some of a droplet's own pixels would pass the gate and
 * some wouldn't, biting a piece out of an otherwise-round shape). The
 * tradeoff is that the falloff becomes granular per-droplet (one whole
 * droplet's worth of density at a time) rather than perfectly smooth per
 * pixel — the correct trade for a spatter effect, where real spray falls
 * off in discrete droplet density rather than a smooth gradient.
 */
export const airbrushScatterInk = (airbrush, stampIndex, stampCenterX, stampCenterY, radius, nx, ny) => {
    const { grain, seed, pointIndex, peakDensityLevel } = airbrush;
    const cellSize = airbrushCellSize(grain);
    const px = nx + 0.5;
    const py = ny + 0.5;
    const homeCellX = nx >> grain;
    const homeCellY = ny >> grain;
    for (let dcy = -1; dcy <= 1; dcy++) {
        const cellY = homeCellY + dcy;
        for (let dcx = -1; dcx <= 1; dcx++) {
            const cellX = homeCellX + dcx;
            const droplet = airbrushDroplet(seed, pointIndex, stampIndex, cellX, cellY, cellSize);
            const gateDx = droplet.centerX - stampCenterX;
            const gateDy = droplet.centerY - stampCenterY;
            const density = airbrushDensityLevelAt(gateDx, gateDy, radius, peakDensityLevel);
            if (droplet.gateThreshold >= density)
                continue; // no droplet in this cell for this stamp
            const ddx = px - droplet.centerX;
            const ddy = py - droplet.centerY;
            if (ddx * ddx + ddy * ddy < droplet.radius * droplet.radius)
                return true;
        }
    }
    return false;
};
/**
 * Ink decision for one pixel of an airbrush stamp — the single entry point
 * `rasterizer.ts` calls for both the binary (`stampOnce`) and AA
 * (`stampOnceAA`) paths. Dispatches to the grain=0 fast path (original
 * single-hash-per-pixel dither — see the module doc above for why it's left
 * unchanged) or the grain>=1 scatter kernel.
 */
export const airbrushIsInk = (airbrush, stampIndex, stampCenterX, stampCenterY, radius, nx, ny) => {
    if (airbrush.grain === 0) {
        const t = airbrushThresholdWord(airbrush.seed, airbrush.pointIndex, stampIndex, nx, ny);
        const dx = nx + 0.5 - stampCenterX;
        const dy = ny + 0.5 - stampCenterY;
        const density = airbrushDensityLevelAt(dx, dy, radius, airbrush.peakDensityLevel);
        return t < density;
    }
    return airbrushScatterInk(airbrush, stampIndex, stampCenterX, stampCenterY, radius, nx, ny);
};
const createAirbrushScatterTable = () => ({
    cellX0: 0,
    cellY0: 0,
    cols: 0,
    rows: 0,
    capacity: 0,
    centerX: new Float64Array(0),
    centerY: new Float64Array(0),
    radiusSq: new Float64Array(0),
    active: new Uint8Array(0),
});
/**
 * Module-level scratch, reused across every stamp/stroke in this JS realm
 * (one per Worker / server snapshot worker — never shared across threads).
 * See the doc block above for why this is safe.
 */
const scatterTable = createAirbrushScatterTable();
const ensureScatterTableCapacity = (t, n) => {
    if (t.capacity >= n)
        return;
    t.centerX = new Float64Array(n);
    t.centerY = new Float64Array(n);
    t.radiusSq = new Float64Array(n);
    t.active = new Uint8Array(n);
    t.capacity = n;
};
/**
 * Populate the module-level scatter table with every grain-cell droplet in
 * `[cellX0,cellX1] x [cellY0,cellY1]` for ONE stamp. Call ONCE per stamp
 * (`stampOnce`/`stampOnceAA` in `rasterizer.ts`), BEFORE the per-pixel loop,
 * with a cell range covering every pixel the stamp will actually iterate
 * PLUS the 1-cell neighbour margin (`airbrushScatterInk`'s closed-form proof
 * that a 3×3 cell search is sufficient — see that function's doc).
 *
 * Per-cell math is IDENTICAL to `airbrushScatterInk`'s per-pixel body (same
 * `airbrushDroplet` call, same `airbrushDensityLevelAt` gate, same operand
 * order) — only WHEN it runs changes. `radiusSq` is stored instead of
 * `radius` (hoists the `radius*radius` multiply out of the per-pixel test
 * too) — same float value, computed the same way, just once instead of once
 * per visiting pixel.
 */
export const prepareAirbrushScatterTable = (airbrush, stampIndex, stampCenterX, stampCenterY, radius, cellX0, cellY0, cellX1, cellY1) => {
    const cols = cellX1 - cellX0 + 1;
    const rows = cellY1 - cellY0 + 1;
    const n = cols * rows;
    ensureScatterTableCapacity(scatterTable, n);
    scatterTable.cellX0 = cellX0;
    scatterTable.cellY0 = cellY0;
    scatterTable.cols = cols;
    scatterTable.rows = rows;
    const { grain, seed, pointIndex, peakDensityLevel } = airbrush;
    const cellSize = airbrushCellSize(grain);
    const { centerX, centerY, radiusSq, active } = scatterTable;
    let idx = 0;
    for (let cy = cellY0; cy <= cellY1; cy++) {
        for (let cx = cellX0; cx <= cellX1; cx++, idx++) {
            const droplet = airbrushDroplet(seed, pointIndex, stampIndex, cx, cy, cellSize);
            const gateDx = droplet.centerX - stampCenterX;
            const gateDy = droplet.centerY - stampCenterY;
            const density = airbrushDensityLevelAt(gateDx, gateDy, radius, peakDensityLevel);
            const isActive = droplet.gateThreshold < density;
            active[idx] = isActive ? 1 : 0;
            if (isActive) {
                centerX[idx] = droplet.centerX;
                centerY[idx] = droplet.centerY;
                radiusSq[idx] = droplet.radius * droplet.radius;
            }
        }
    }
};
/**
 * Per-pixel scatter ink lookup against the table `prepareAirbrushScatterTable`
 * most recently filled — same 3×3 neighbourhood search as `airbrushScatterInk`
 * (same iteration order: `dcy` outer, `dcx` inner, first-hit `return true`),
 * but reading precomputed droplets instead of re-hashing. The caller MUST
 * have called `prepareAirbrushScatterTable` for the SAME stamp (same
 * `airbrush`/`stampIndex`/`stampCenterX`/`stampCenterY`/`radius`) with a cell
 * range covering `(nx, ny)`'s home cell ± 1 — `rasterizer.ts`'s `stampOnce`/
 * `stampOnceAA` do so unconditionally before iterating pixels.
 */
export const airbrushScatterInkFromTable = (grain, nx, ny) => {
    // Hoisted to locals (not `t.foo` per access) — same values, cheaper reads
    // in the per-pixel hot loop. `as number`: the index is always within
    // `[0, cols*rows)` by construction (`prepareAirbrushScatterTable` sized the
    // arrays to `cols*rows` and this function's `row`/`col` bounds checks keep
    // `idx` in that same range) — TS's `noUncheckedIndexedAccess` can't see
    // that invariant, so we assert it instead of paying a runtime `?? 0` branch
    // on every pixel.
    const { cellX0, cellY0, cols, rows, active, centerX, centerY, radiusSq } = scatterTable;
    const px = nx + 0.5;
    const py = ny + 0.5;
    const homeCellX = nx >> grain;
    const homeCellY = ny >> grain;
    for (let dcy = -1; dcy <= 1; dcy++) {
        const row = homeCellY + dcy - cellY0;
        if (row < 0 || row >= rows)
            continue;
        const rowBase = row * cols;
        for (let dcx = -1; dcx <= 1; dcx++) {
            const col = homeCellX + dcx - cellX0;
            if (col < 0 || col >= cols)
                continue;
            const idx = rowBase + col;
            if (active[idx] === 0)
                continue;
            const ddx = px - centerX[idx];
            const ddy = py - centerY[idx];
            if (ddx * ddx + ddy * ddy < radiusSq[idx])
                return true;
        }
    }
    return false;
};
/**
 * Fast-path ink decision: `grain === 0` is untouched (already cheap, one hash
 * per pixel — see `airbrushIsInk`'s doc for why the scatter search is skipped
 * there entirely). `grain >= 1` reads the module-level scatter table instead
 * of re-hashing per pixel — the CALLER is responsible for having called
 * `prepareAirbrushScatterTable` for this exact stamp first (`rasterizer.ts`
 * does so unconditionally in `stampOnce`/`stampOnceAA` before the pixel
 * loop). `airbrushIsInk` above remains the reference implementation used by
 * tests and any other caller that doesn't want to manage the table's
 * lifecycle.
 */
export const airbrushIsInkFast = (airbrush, stampIndex, stampCenterX, stampCenterY, radius, nx, ny) => {
    if (airbrush.grain === 0) {
        const t = airbrushThresholdWord(airbrush.seed, airbrush.pointIndex, stampIndex, nx, ny);
        const dx = nx + 0.5 - stampCenterX;
        const dy = ny + 0.5 - stampCenterY;
        const density = airbrushDensityLevelAt(dx, dy, radius, airbrush.peakDensityLevel);
        return t < density;
    }
    return airbrushScatterInkFromTable(airbrush.grain, nx, ny);
};
//# sourceMappingURL=airbrushHash.js.map